import { useCallback, useEffect, useState } from 'react';
import type { User } from '../../../types';
import { searchActiveUsers } from '../../../utils/searchUsers';

const DEFAULT_LIMIT = 40;

/**
 * Lista de usuários ativos para os selects do módulo mudanças.
 *
 * Quatro telas repetiam a mesma chamada com o token lido do localStorage e sem
 * tratamento de erro nem cancelamento — um unmount durante a request deixava
 * `setState` em componente desmontado.
 */
export function useActiveUsers(options?: { limit?: number; enabled?: boolean }) {
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const enabled = options?.enabled ?? true;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const items = await searchActiveUsers('', localStorage.getItem('token'), {
          limit,
          signal,
        });
        if (!signal?.aborted) setUsers(items);
      } catch {
        // Select de apoio: sem usuários a tela continua utilizável.
        if (!signal?.aborted) setUsers([]);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [enabled, load]);

  return { users, loading, reload: load };
}
